<template>
  <div
    v-if="productRawId"
    class="jdgm-widget jdgm-review-widget jdgm-outside-widget"
    :class="{ 'jdgm-no-reviews': !showCustomerReview }"
    :key="productRawId"
    :data-id="productRawId"
  ></div>
</template>

<script setup lang="ts">
import { appendChildElementIntoParentElement, injectScript } from '@/utils/dom'
import useAppendComponent from '@/composables/appendComponent'
import SubmitReviewForm from './SubmitReviewForm.vue'
import ProductReviewButton from './ProductReviewButton.vue'
import { getCurrentInstance, nextTick, onMounted, computed, ref } from 'vue'
import useProductStore from '@/store/product'
import { getRawIdFromShopifyId } from '@/utils/product'
import useRouteStore from '@/store/route'
import {
  SSR_LOAD_TYPE,
  JUDGEME_SHOPIFY_DOMAIN,
  JUDGEME_PUBLIC_TOKEN,
  EVENT_SHOW_AVERAGE_START_PRE_BAGDE,
} from '@/utils'
import EventBus from '@/services/eventbus'

const { renderComponent } = useAppendComponent()
const { appContext } = getCurrentInstance()
const productStore = useProductStore()
const routeStore = useRouteStore()

const showCustomerReview = ref(true)

const productRawId = computed(() =>
  getRawIdFromShopifyId(productStore.product?.id)
)

onMounted(async () => {
  function injectScriptJudgeme() {
    // Because the JUDGEME_PUCLIC_TOKEN variable when taken from gg cloud config does not get the value, so I add this config hard code
    const judgemePublicTokens = [
      {
        shop: 'sunfloweric.myshopify.com',
        token: '8IpwB36WcQ1Jc8IBng3LwK46QyA',
      },
      {
        shop: 'fiverprints.myshopify.com',
        token: 'McXWswSWxAv5qtZB-pwYry6oWEE',
      },
      {
        shop: 'silveryprinting.myshopify.com',
        token: 'mVbUnuo4wzNpV_OsgmhuAhqfNQU',
      },
      {
        shop: 'sunflowerful.myshopify.com',
        token: 'gg78VFilLtyl2UPZt5Z1yiOAs18',
      },
      {
        shop: 'silveryhlw.myshopify.com',
        token: 'wjKMIA25UXsXzERDVhb7Iwziup4',
      },
      {
        shop: 'halloween-hoard.myshopify.com',
        token: 'xrEEK17ryF2kyUrjBLu7OR_B94U',
      },
    ]
    let judgemePublicToken =
      judgemePublicTokens.find((item) => item.shop == JUDGEME_SHOPIFY_DOMAIN)
        ?.token || ''

    const scripts = document.createElement('script')
    scripts.innerHTML = `jdgm=window.jdgm || {};jdgm.SHOP_DOMAIN='${JUDGEME_SHOPIFY_DOMAIN}';jdgm.PLATFORM='shopify';jdgm.PUBLIC_TOKEN='${
      JUDGEME_PUBLIC_TOKEN || judgemePublicToken
    }'`
    document.head.appendChild(scripts)

    setTimeout(() => {
      injectScript('https://cdn.judge.me/widget_preloader.js', undefined, true)
      injectScript('https://cdn1.judge.me/assets/installed.js', undefined, true)
    })
  }
  if (routeStore.loadType == SSR_LOAD_TYPE) {
    await injectScriptJudgeme()
    waitForReloadToComplete(addAverageStarAndButtonReview)
  } else {
    if (!window.jdgm) await injectScriptJudgeme()
    setTimeout(async () => {
      if (window.jdgmCacheServer) {
        await window.jdgmCacheServer.reloadAll()
      }
      waitForReloadToComplete(addAverageStarAndButtonReview)
    }, 3000)
  }
})

function waitForReloadToComplete(callback) {
  const originalReviewCount = 0
  const checkInterval = 100
  const maxWaitTime = 7000
  let waitedTime = 0

  const intervalId = setInterval(() => {
    const jdgmPrevBadge = document.querySelector(
      '.jdgm-preview-badge .jdgm-prev-badge'
    )
    const currentReviewCount =
      jdgmPrevBadge?.getAttribute('data-number-of-reviews') || 0

    if (
      currentReviewCount != originalReviewCount ||
      waitedTime >= maxWaitTime
    ) {
      clearInterval(intervalId)
      callback()
    }
    waitedTime += checkInterval
  }, checkInterval)
}

async function addAverageStarAndButtonReview() {
  try {
    const { averageRating, numberOfReviews } = getRatingAndReviewCount()

    if (numberOfReviews == 0) {
      showCustomerReview.value = false
    } else {
      showCustomerReview.value = true
    }

    const elementPrevBadgeWrapper = document.querySelector(
      '.jdgm-prev-badge__stars'
    )

    if (elementPrevBadgeWrapper) {
      const customAverageStarAndTotalReviewElement =
        document.createElement('span')
      customAverageStarAndTotalReviewElement.classList.add('prev-badge-wrapper')
      customAverageStarAndTotalReviewElement.innerHTML = `<span class="average-star-bagde">${averageRating}</span><span>(${numberOfReviews} reviews)</span>`
      elementPrevBadgeWrapper.appendChild(
        customAverageStarAndTotalReviewElement
      )
    }

    EventBus.trigger(EVENT_SHOW_AVERAGE_START_PRE_BAGDE)

    const elementSummaryTextWrapper = document.querySelector(
      '.jdgm-rev-widg__summary-stars'
    )
    if (elementSummaryTextWrapper) {
      const customAverageStarElement = document.createElement('span')
      customAverageStarElement.classList.add('average-star-wrapper')
      customAverageStarElement.innerHTML = `<span class="average-star">${averageRating}<span class="five-star">/5.0</span></span>`
      elementSummaryTextWrapper.appendChild(customAverageStarElement)
    }

    const elementWrapper = document.querySelector('.jdgm-rev-widg__header')
    if (!elementWrapper) return
    const customeElement = document.createElement('div')
    customeElement.setAttribute('id', `customer-review-form`)
    appendChildElementIntoParentElement(elementWrapper, customeElement, 6)
    await nextTick()
    const elementReviewForm = document.querySelector('#customer-review-form')
    if (!elementReviewForm) return
    const renderComponentProductReviewForm = () =>
      renderComponent({
        element: elementReviewForm,
        component: SubmitReviewForm,
        props: {},
        appContext,
      })
    renderComponentProductReviewForm()

    const elementSummaryWrapper = document.querySelector(
      '.jdgm-rev-widg__header'
    )
    if (!elementSummaryWrapper) return
    const customProductReviewButtonElement = document.createElement('div')
    customProductReviewButtonElement.setAttribute('id', 'product-review-button')
    customProductReviewButtonElement.setAttribute('class', 'hidden-md-down')
    appendChildElementIntoParentElement(
      elementSummaryWrapper,
      customProductReviewButtonElement,
      7
    )
    await nextTick()
    const elementProductReviewButton = document.querySelector(
      '#product-review-button'
    )
    if (!elementProductReviewButton) return
    const renderComponentProductReviewButton = () =>
      renderComponent({
        element: elementProductReviewButton,
        component: ProductReviewButton,
        props: {},
        appContext,
      })

    renderComponentProductReviewButton()
  } catch (error) {
    // do nothing
  }
}

function getRatingAndReviewCount() {
  const prevBadgeElement = document.querySelector('.jdgm-prev-badge')
  if (prevBadgeElement) {
    const averageRating = prevBadgeElement.getAttribute('data-average-rating')
    const numberOfReviews = prevBadgeElement.getAttribute(
      'data-number-of-reviews'
    )
    return {
      averageRating: averageRating ? Number(averageRating).toFixed(1) : '0.0',
      numberOfReviews: numberOfReviews ? numberOfReviews : 0,
    }
  }
  return {
    averageRating: 0,
    numberOfReviews: 0,
  }
}
</script>

<style lang="scss">
.jdgm-rev {
  @include media-md-up {
    margin-top: 1.5rem !important;
    padding-top: 2rem !important;
  }
  &__header {
    display: grid;
    gap: 0.5rem;
    margin-bottom: 0 !important;
    grid-template-columns: 30px auto;
    grid-template-areas:
      'a d'
      'b b'
      'c c';

    @include media-md-up {
      grid-template-columns: 65px auto;
      grid-template-areas:
        'a d'
        'a b'
        'a c';
    }
    .jdgm-rev {
      &__br,
      &__buyer-badge-wrapper {
        display: none;
      }
      &__icon {
        grid-area: a;
        width: 2rem !important;
        height: 2rem !important;
        line-height: 2rem !important;
        @include media-md-up {
          width: 3.2rem !important;
          height: 3.2rem !important;
          line-height: 3.2rem !important;
        }
      }
      &__rating {
        line-height: 1;
        grid-area: b;
      }
      &__timestamp {
        grid-area: c;
        color: var(--text-color-tertiary);
      }
      &__author-wrapper {
        grid-area: d;
        font-size: 14px;
        line-height: 1;
        color: var(--color-primary);
        display: grid;
        align-items: center;
        .jdgm-rev__author {
          line-height: 28px;
        }
        .jdgm-rev__location {
          display: none;
        }
        @include media-md-up {
          font-size: 12px;
          .jdgm-rev__author {
            line-height: 1.4;
          }
        }
      }
      &__prod-info-wrapper {
        display: none !important;
      }
    }
  }

  &__custom-form {
    display: none;
  }
  &-widg {
    border: none !important;
    padding: 0 !important;
    margin: 0 !important;
  }
  &__rating {
    .jdgm-star {
      display: inline-block !important;
      margin-right: 4px !important;
      width: 1em;
      height: 1em;
      &.jdgm--off,
      &.jdgm--half {
        display: none !important;
      }
      &.jdgm--on {
        background-image: url('@/assets/images/icons/icon-star-orange.png');
      }
    }
  }
  &__pic-link {
    height: 80px !important;
    margin-right: 8px !important;
  }
  &__title {
    margin-bottom: 4px !important;
  }
  &__title,
  &__body,
  &__pics {
    margin-left: 0 !important;
    @include media-md-up {
      margin-left: calc(65px + 0.5rem) !important;
    }
  }
  &__timestamp {
    margin-left: unset !important;
    font-size: 12px;
  }
}

.jdgm-rev-widg {
  &__header {
    display: grid;
    grid-template-columns: repeat(2, 1fr);
    grid-template-areas:
      'a a'
      'b b'
      'c c'
      'e e'
      'd d';

    @include media-md-up {
      grid-template-areas:
        'a a'
        'b c'
        'f c'
        'e e'
        'd d';
    }
  }
  &__title {
    grid-area: a;
    text-align: center;
    margin-bottom: 1rem !important;
    margin-top: 1rem !important;
    @include media-md-up {
      margin-bottom: 2rem !important;
      margin-top: 0 !important;
    }
  }
  &__summary {
    grid-area: b;
    display: grid;
    justify-content: center;
  }
  &__sort-wrapper {
    grid-area: d;
    margin-top: 1.5rem !important;
    padding-top: 1rem;
    border-top: 1px solid var(--border-color);
    @include media-md-up {
      padding-top: 1.5rem;
    }
  }
  .jdgm-histogram {
    grid-area: c;
    display: grid;
    justify-content: center;
    margin-top: 2rem !important;
    padding: 0;
    @include media-md-up {
      margin: 0;
      margin-top: 0 !important;
    }
    &__clear-filter {
      text-align: center;
      margin-top: 0.5rem;
    }
    &__bar {
      width: calc(100% - 120px) !important;
      max-width: 400px;
    }
  }
  #customer-review-form {
    grid-area: e;
  }
  #product-review-button {
    grid-area: f;
  }
  .jdgm-star {
    display: inline-block !important;
    margin-right: 4px !important;
  }
}

.jdgm-star {
  display: inline-block !important;
  width: 1.25em;
  height: 1.25em;
  background-size: 100%;
  background-position: 0 0;
  background-repeat: no-repeat;
  margin-right: 4px !important;
  &.jdgm--off {
    background-image: url('@/assets/images/icons/icons-star-empty.png');
    &::before {
      content: '' !important;
    }
  }
  &.jdgm--on {
    background-image: url('@/assets/images/icons/icon-star-orange.png');
    &::before {
      content: '' !important;
    }
  }
  &.jdgm--half {
    &::before {
      content: '' !important;
    }
    background-image: url('@/assets/images/icons/icon-star-half-empty-orange.png');
  }
}

.jdgm-histogram {
  &__row {
    display: flex;
    justify-content: center;
    margin-bottom: 4px !important;

    &--selected {
      font-weight: 700 !important;
    }

    .jdgm-histogram {
      &__star > * {
        display: none !important;
      }
      &__percentage,
      &__frequency {
        width: 35px;
      }
      &__star::before {
        display: inline-block;
        width: 35px;
      }
    }
    &:nth-child(1) {
      .jdgm-histogram__star::before {
        content: '5-star';
      }
    }
    &:nth-child(2) {
      .jdgm-histogram__star::before {
        content: '4-star';
      }
    }
    &:nth-child(3) {
      .jdgm-histogram__star::before {
        content: '3-star';
      }
    }
    &:nth-child(4) {
      .jdgm-histogram__star::before {
        content: '2-star';
      }
    }
    &:nth-child(5) {
      .jdgm-histogram__star::before {
        content: '1-star';
      }
    }
  }
  &__bar {
    height: 10px !important;
    margin: auto 0.5rem !important;
  }
  &__bar-content,
  &__bar:after {
    background: #000000 !important;
    height: 10px !important;
  }
}

.jdgm-rev-widg {
  &__summary-stars {
    text-align: center;
    display: flex;
    margin-bottom: 4px !important;
  }
  &__summary-inner {
    display: flex;
    flex-direction: column;
    gap: 0;
    height: 2rem;
    align-items: center;
    @include media-xl-up {
      flex-direction: row;
      gap: 1rem;
      &::before {
        content: 'Average Rating';
        font-size: 20px;
        font-weight: bold;
        position: relative;
        top: -3px;
      }
    }
    .jdgm-rev-widg__summary-text {
      font-size: 14px;
      font-weight: 500;
      display: flex;
      align-items: center;
      @include media-md-up {
        font-size: 12px;
      }
    }
  }
}
.jdgm-rev__pic-link.jdgm--loading::after {
  width: 40px !important;
  height: 40px !important;
}
.jm-mfp-wrap,
.jm-mfp-bg {
  z-index: 9999 !important;
}
.jdgm-widget-actions-wrapper {
  display: none;
}
.jdgm-rev-widg__summary,
.jdgm-histogram {
  float: unset !important;
  border: none !important;
}

img.jdgm-rev__pic-img {
  width: 80px;
  height: 80px;
  object-fit: cover;
}
.jdgm-paginate__page {
  min-width: unset !important;
}

.jdgm-prev-badge__stars::after {
  display: none;
}

.prev-badge-wrapper {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  font-size: 12px;
  font-weight: 600;
  position: relative;
  top: 1px;
  padding-left: 2px;

  .average-star-bagde {
    font-weight: 700;
    font-size: 18px;
    top: 0;
  }

  span {
    position: relative;
    top: 1px;
  }
}

.average-star-wrapper {
  height: 0;
  .average-star {
    font-weight: 700;
    font-size: 20px;
    position: relative;
    line-height: 0;
    top: 3px;
    margin-left: 0.75rem;
    @include media-xl-up {
      top: -5px;
    }
  }
  .five-star {
    display: none;
    @include media-xl-up {
      display: inline-block;
    }
  }
}
.jdgm-no-reviews {
  .average-star {
    top: 0px;
  }
}
.jdgm-rev-widg__title {
  font-size: 22px;
  @include media-md-up {
    font-size: 2rem;
  }
}
</style>
